/**
 * User Process Handlers
 *
 * All user-related query and mutation handlers
 */

import { handleRequest } from './config'
import { mapBackendUserToFrontendUser, mapFrontendUserToBackendUser } from './typeMappers'
import { mockData } from '../mocks'
import { ApiResponse, User } from '../types'
import { httpsCallable } from 'firebase/functions'
import { functions, auth } from '../../libs/Firebase'
import * as handlers from '../handlers'
import { User as BackendUser } from '../../../../pure-js/types/User'
import { FeedbackStatusLevel } from '../../../../pure-js/enums/FeedbackLevel'
import type {
  GetUserProfileReportRequest,
  GetUserProfileReportResponse,
  UserProfileReportData
} from '../../../../pure-js/types/UserProfileReport'

// ========================================
// QUERY HANDLERS
// ========================================

/**
 * Get all users with optional filters
 */
export const getUsers = async (_filters?: Record<string, string | number | boolean>): Promise<ApiResponse<User[]>> => {
  return handleRequest(
    () => Promise.resolve({ data: mockData.users, success: true } as ApiResponse<User[]>),
    async () => {
      try {
        const callable = httpsCallable(functions, 'getUsers')
        const result = await callable({})

        const data = result.data as { success: boolean; users: BackendUser[] }

        if (!data.success || !data.users) {
          return {
            data: [],
            success: false,
            error: 'Failed to fetch users from backend'
          }
        }

        // Map backend users to frontend users
        const mappedUsers = data.users.map(mapBackendUserToFrontendUser)

        return {
          data: mappedUsers,
          success: data.success,
          error: data.success ? undefined : 'Failed to fetch users'
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error)

        return {
          data: [],
          success: false,
          error: message || 'Unknown error occurred while fetching users'
        }
      }
    }
  )
}

/**
 * Get a single user by ID
 */
export const getUser = async (id: string): Promise<ApiResponse<User>> => {
  return handleRequest(
    () => {
      const user = mockData.users.find((u) => u.id === id)

      return Promise.resolve({
        data: user!,
        success: !!user,
        error: user ? undefined : 'User not found'
      } as ApiResponse<User>)
    },
    async () => {
      try {
        const callable = httpsCallable(functions, 'getUser')
        const result = await callable({ userId: id })

        const data = result.data as { success: boolean; user: BackendUser }

        if (!data.success || !data.user) {
          return {
            data: {} as User,
            success: false,
            error: 'Failed to fetch user from backend'
          }
        }

        // Map backend user to frontend user
        const mappedUser = mapBackendUserToFrontendUser(data.user)

        return {
          data: mappedUser,
          success: true
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error)
        return {
          data: {} as User,
          success: false,
          error: message || 'Unknown error occurred while fetching user'
        }
      }
    }
  )
}

/**
 * Get the currently authenticated user
 */
export const getCurrentUser = async (): Promise<ApiResponse<User>> => {
  return handleRequest(
    () => Promise.resolve({ data: mockData.users[0], success: true } as ApiResponse<User>),
    async () => {
      const uid = auth.currentUser?.uid
      if (!uid) {
        return { data: {} as User, success: false, error: 'Not authenticated' }
      }
      return getUser(uid)
    }
  )
}

/**
 * Get the current user's profile
 */
export const getUserProfile = async (): Promise<ApiResponse<User>> => {
  return handleRequest(
    () => Promise.resolve({ data: mockData.users[0], success: true } as ApiResponse<User>),
    async () => {
      // TODO: Implement cloud function for user profile
      return { data: {} as User, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * Get user profile report with collaboration profile data
 */
export const getUserProfileReport = async (
  request?: GetUserProfileReportRequest
): Promise<ApiResponse<UserProfileReportData & { ready: boolean }>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: {
          ready: true,
          topStrengths: [
            {
              variable: 'planEvaluation',
              normalizedScore: 0.85,
              value: 85,
              statusLevel: FeedbackStatusLevel.EXCELLENT
            },
            { variable: 'roleTalk', normalizedScore: 0.78, value: 78, statusLevel: FeedbackStatusLevel.GOOD }
          ],
          profileSummary: 'Test profile summary',
          selfPerceivedPersonality: 'Test personality description',
          whatIsWorking: {
            sections: ['Section 1 text', 'Section 2 text', 'Section 3 text'],
            summary: 'Summary text'
          },
          improvementAreas: {
            sections: ['Improvement 1', 'Improvement 2', 'Improvement 3'],
            summary: 'Improvement summary'
          },
          tryThisAtWork: ['Recommendation 1', 'Recommendation 2', 'Recommendation 3']
        } as UserProfileReportData & { ready: boolean },
        success: true
      } as ApiResponse<UserProfileReportData & { ready: boolean }>),
    async (): Promise<ApiResponse<UserProfileReportData & { ready: boolean }>> => {
      try {
        const callable = httpsCallable<GetUserProfileReportRequest, GetUserProfileReportResponse>(
          functions,
          'getUserProfileReport'
        )
        const result = await callable(request || {})

        if (!result.data.success) {
          return {
            data: {} as UserProfileReportData & { ready: boolean },
            success: false,
            error: result.data.error || 'Failed to fetch user profile report'
          }
        }

        return {
          data: {
            ...result.data.data,
            ready: result.data.ready
          } as UserProfileReportData & { ready: boolean },
          success: true,
          error: result.data.ready ? undefined : 'Profile report is not ready yet'
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error)
        return {
          data: {} as UserProfileReportData & { ready: boolean },
          success: false,
          error: message || 'Unknown error occurred while fetching user profile report'
        }
      }
    }
  )
}

// ========================================
// MUTATION HANDLERS
// ========================================

/**
 * Create a new user
 */
export const createUser = async (userData: Partial<User>): Promise<ApiResponse<User>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: {
          ...userData,
          id: 'new-user-id',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as User,
        success: true
      } as ApiResponse<User>),
    async () => {
      // TODO: Implement cloud function for user creation
      return { data: {} as User, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * Update an existing user
 * NOTE: 'profileImage', 'language' and 'preferences' go through the updateUser Cloud Function.
 * For other fields (e.g., completedOnboardingSteps, displayName), we use direct Firestore writes.
 */
export const updateUser = async (id: string, userData: Partial<User>): Promise<ApiResponse<User>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: { ...userData, id, updatedAt: new Date().toISOString() } as User,
        success: true
      } as ApiResponse<User>),
    async () => {
      try {
        // Map frontend user data to backend format
        const backendPayload = mapFrontendUserToBackendUser(userData)

        // Check if payload contains only Cloud Function-allowed fields
        // Cloud Function allows: profileImage, language, preferences (for non-admin users)
        const cloudFunctionAllowedFields = ['profileImage', 'language', 'preferences']
        const payloadKeys = Object.keys(backendPayload)
        const useCloudFunction = payloadKeys.every((key) => cloudFunctionAllowedFields.includes(key))

        if (useCloudFunction && payloadKeys.length > 0) {
          // Use Cloud Function for allowed fields

          const callable = httpsCallable(functions, 'updateUser')
          const result = await callable({ userId: id, payload: backendPayload })
          const data = result.data as { success: boolean }

          if (!data.success) {
            return {
              data: {} as User,
              success: false,
              error: 'Failed to update user via Cloud Function'
            }
          }
        } else {
          // Use direct Firestore write for other fields (e.g., completedOnboardingSteps)

          const result = await handlers.directDb.updateUser({ id, ...backendPayload })

          if (!result.success) {
            return {
              data: {} as User,
              success: false,
              error: result.error || 'Failed to update user via Firestore'
            }
          }
        }

        // Return the updated user data in frontend format
        return {
          data: { ...userData, id, updatedAt: new Date().toISOString() } as User,
          success: true
        }
      } catch (error) {
        return {
          data: {} as User,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }
    }
  )
}

/**
 * Delete a user
 */
export const deleteUser = async (_id: string): Promise<ApiResponse<void>> => {
  return handleRequest(
    () => Promise.resolve({ data: undefined, success: true } as ApiResponse<void>),
    async () => {
      // TODO: Implement cloud function for user deletion
      return { data: undefined, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * Batch create multiple users
 */
export const batchCreateUsers = async (usersData: Partial<User>[]): Promise<ApiResponse<User[]>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: usersData.map((user, index) => ({
          ...user,
          id: `batch-user-${index}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })) as User[],
        success: true
      } as ApiResponse<User[]>),
    async () => {
      // TODO: Implement cloud function for batch user creation
      return { data: [], success: false, error: 'Not implemented' }
    }
  )
}
